import { Component } from "@theme/component";
import { ThemeEvents, MediaStartedPlayingEvent } from "@theme/events";
import { DialogCloseEvent } from "@theme/dialog";

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();
  #hasSetupMediaListeners = false;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      this.pauseMedia.bind(this),
      { signal }
    );
    window.addEventListener(
      DialogCloseEvent.eventName,
      this.pauseMedia.bind(this),
      { signal }
    );

    if (this.hasAttribute("autoplay")) {
      this.setAttribute("data-media-loaded", "true");
      this.showDeferredMedia();
    }

    // Setup autoplay when visible
    if (this.hasAttribute("data-autoplay-when-visible")) {
      this.#setupAutoplayWhenVisible();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }
  }

  /**
   * Setup intersection observer for autoplay when visible
   */
  #setupAutoplayWhenVisible() {
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Load and play video when it enters view
            if (!this.getAttribute("data-media-loaded")) {
              this.loadContent(false);
            }
            this.playMedia();
            this.classList.add("playing");
          } else {
            // Pause when out of view
            this.pauseMedia();
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% of video is visible
      }
    );

    this.#intersectionObserver.observe(this);
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove("hidden");
      const playIcon = toggleMediaButton.querySelector(".icon-play");
      if (playIcon) playIcon.classList.toggle("hidden", isPlaying);
      const pauseIcon = toggleMediaButton.querySelector(".icon-pause");
      if (pauseIcon) pauseIcon.classList.toggle("hidden", !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    // Don't assume it's playing - let the media events tell us
    // This is especially important for iOS Safari with battery saver
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    // Check if media already exists in the DOM (not in template)
    const existingVideo = this.querySelector("video");
    const existingIframe = this.querySelector("iframe[data-video-type]");
    const hasExistingMedia = existingVideo || existingIframe;

    // If media already loaded (but not from template), just setup listeners
    if (hasExistingMedia && this.getAttribute("data-media-loaded")) {
      this.#setupMediaStateListeners();

      // Check if video should autoplay
      if (existingVideo && existingVideo.getAttribute("autoplay")) {
        const playPromise = existingVideo.play();
        if (playPromise !== undefined) {
          return playPromise
            .then(() => {
              console.log("playPromise success");
              this.isPlaying = true;
              this.updatePlayPauseHint(this.isPlaying);
            })
            .catch(() => {
              console.log("playPromise failed");
              this.isPlaying = false;
              this.updatePlayPauseHint(this.isPlaying);
            });
        }
      }
      return;
    }

    // If already loaded from template, don't load again
    if (this.getAttribute("data-media-loaded")) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content =
      this.querySelector("template")?.content.firstElementChild?.cloneNode(
        true
      );

    if (!content) return;

    this.setAttribute("data-media-loaded", "true");
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add("deferred-media__playing");

    // Setup listeners to track actual playback state
    this.#setupMediaStateListeners();

    if (
      content instanceof HTMLVideoElement &&
      content.getAttribute("autoplay")
    ) {
      // Attempt autoplay for safari
      const playPromise = content.play();

      // Handle autoplay failure (e.g., iOS battery saver mode)
      if (playPromise !== undefined) {
        return playPromise
          .then(() => {
            // Autoplay succeeded
            this.isPlaying = true;
            this.updatePlayPauseHint(this.isPlaying);
          })
          .catch(() => {
            // Autoplay failed - video is not playing
            this.isPlaying = false;
            this.updatePlayPauseHint(this.isPlaying);
          });
      }
    }
  }

  /**
   * Setup listeners to track actual media playback state
   */
  #setupMediaStateListeners() {
    if (this.#hasSetupMediaListeners) return;
    this.#hasSetupMediaListeners = true;

    const signal = this.#abortController.signal;
    const video = this.querySelector("video");
    const iframe = this.querySelector("iframe[data-video-type]");

    if (video) {
      // Listen to native video events
      video.addEventListener(
        "play",
        () => {
          this.isPlaying = true;
          this.updatePlayPauseHint(this.isPlaying);
          // For autoplay when visible, add playing class to hide poster
          if (this.hasAttribute("data-autoplay-when-visible")) {
            this.classList.add("playing");
          }
        },
        { signal }
      );

      video.addEventListener(
        "pause",
        () => {
          this.isPlaying = false;
          this.updatePlayPauseHint(this.isPlaying);
          // For autoplay when visible, remove playing class to show poster
          if (this.hasAttribute("data-autoplay-when-visible")) {
            this.classList.remove("playing");
          }
        },
        { signal }
      );

      video.addEventListener(
        "ended",
        () => {
          this.isPlaying = false;
          this.updatePlayPauseHint(this.isPlaying);
          // For autoplay when visible, remove playing class to show poster
          if (this.hasAttribute("data-autoplay-when-visible")) {
            this.classList.remove("playing");
          }
        },
        { signal }
      );
    } else if (iframe) {
      // For YouTube/Vimeo, listen to postMessage events
      window.addEventListener(
        "message",
        (event) => {
          const data =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;

          // YouTube player state changes
          if (
            data.event === "infoDelivery" &&
            data.info?.playerState !== undefined
          ) {
            // 1 = playing, 2 = paused, 0 = ended
            const isPlaying = data.info.playerState === 1;
            if (this.isPlaying !== isPlaying) {
              this.isPlaying = isPlaying;
              this.updatePlayPauseHint(this.isPlaying);
            }
          }

          // Vimeo player events
          if (data.event === "play") {
            this.isPlaying = true;
            this.updatePlayPauseHint(this.isPlaying);
          } else if (data.event === "pause" || data.event === "ended") {
            this.isPlaying = false;
            this.updatePlayPauseHint(this.isPlaying);
          }
        },
        { signal }
      );
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        "*"
      );
      // State will be updated by event listeners
    } else {
      const video = this.querySelector("video");
      if (video) {
        const playPromise = video.play();
        // Handle play() promise to catch any errors
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn("Video play failed:", error);
            // State will be updated by event listeners
          });
        }
      }
      // State will be updated by event listeners
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector("iframe[data-video-type]");

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === "youtube"
          ? '{"event":"command","func":"' + "pauseVideo" + '","args":""}'
          : '{"method":"pause"}',
        "*"
      );
      // State will be updated by event listeners
    } else {
      this.querySelector("video")?.pause();
      // State will be updated by event listeners
    }
  }
}

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector("model-viewer");
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      "pointerdown",
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      "click",
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(
          distanceX * distanceX + distanceY * distanceY
        );

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get("product-model")) {
  customElements.define("product-model", ProductModel);
}
